import { useState } from "react";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { IoChevronBack } from "react-icons/io5";
import {
    buildBillingCategoryLookup,
    COVERAGE_TIER_MAPPINGS,
    COVERAGE_TIER_SET_NAMES,
    parseDescriptionTier,
} from "../../utils/billingRateUtils.js";

export const currencyFmt = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

// Resolves the coverage tier headers for a benefit plan from the upload context:
// uploadContext.byBenefitPlanHccId[benefitPlanHccId].coverageTierSetName -> mapping key -> tier mapping keys.
export const getCoverageTierHeaders = (uploadContext, benefitPlanHccId) => {
    const coverageTierSetName =
        uploadContext?.byBenefitPlanHccId?.[benefitPlanHccId]?.coverageTierSetName;
    const mappingKey = mappingKeyOf(coverageTierSetName);
    const mapping = mappingKey ? COVERAGE_TIER_MAPPINGS[mappingKey] : null;
    return mapping ? Object.keys(mapping) : [];
};

// Returns { totals, pmpmRates } where:
//   totals    – per-tier summed amounts, excluding PMPM entries
//   pmpmRates – array of { feeDescription, amount } for entries whose feeType is 'PMPM'
export const computeBillingRateTotals = (billingCategoryRates, componentsByName, fees, tierHeaders) => {
    const headers = tierHeaders ?? [];
    const totals = headers.map(() => 0);
    const pmpmRates = [];
    (billingCategoryRates ?? []).forEach((cat) => {
        const codeEntry = cat?.billingCategory?.codeEntry;
        const firstDef = cat?.billingRateDefinitions?.[0];
        const componentName = firstDef?.componentReference?.component ?? "";
        const wrapper = componentsByName?.get(componentName);
        const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
        const rateSet = getRateSet(component);
        if (!rateSet) return;

        // PMPM fees are reported separately rather than added to tier totals.
        if (String(wrapper?.feeType ?? "").toUpperCase() === "PMPM") {
            const amount = Number(rateSet?.defaultRate?.amount ?? 0);
            if (Number.isFinite(amount)) {
                const label = componentName.substring(componentName.indexOf(" ") + 1);
                pmpmRates.push({ label, amount });
            }
            return;
        }

        const fee = codeEntry
            ? (fees ?? []).find((f) => f?.lookup1 === codeEntry || f?.description === codeEntry)
            : null;
        const tier = fee?.lookup2
            ?? parseDescriptionTier(fee?.description)?.tier
            ?? parseDescriptionTier(wrapper?.feeDescription)?.tier
            ?? parseDescriptionTier(componentName)?.tier
            ?? headers.find((t) => componentName.includes(` - ${t}`))
            ?? null;
        if (tier == null) {
            const amount = Number(rateSet?.defaultRate?.amount ?? 0);
            if (Number.isFinite(amount)) {
                for (let i = 0; i < totals.length; i += 1) {
                    totals[i] += amount;
                }
            }
            return;
        }
        const idx = headers.indexOf(tier);
        if (idx < 0) return;
        const amount = Number(rateSet?.specificRates?.[0]?.value?.amount ?? 0);
        if (Number.isFinite(amount)) {
            totals[idx] += amount;
        }
    });
    return { totals, pmpmRates };
};

const resolveBillingRateTotalLine = (cat, componentsByName, fees, tierHeaders) => {
    const codeEntry = cat?.billingCategory?.codeEntry;
    const componentName = cat?.billingRateDefinitions?.[0]?.componentReference?.component ?? "";
    const wrapper = componentsByName?.get(componentName);
    const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
    const rateSet = getRateSet(component);
    if (!rateSet) {
        return { kind: "skipped", componentName, reason: "no rate set" };
    }

    if (String(wrapper?.feeType ?? "").toUpperCase() === "PMPM") {
        const amount = Number(rateSet?.defaultRate?.amount ?? 0);
        return {
            kind: "pmpm",
            componentName,
            amount: Number.isFinite(amount) ? amount : 0,
        };
    }

    const fee = codeEntry
        ? (fees ?? []).find((f) => f?.lookup1 === codeEntry || f?.description === codeEntry)
        : null;
    const tier = fee?.lookup2
        ?? parseDescriptionTier(fee?.description)?.tier
        ?? parseDescriptionTier(wrapper?.feeDescription)?.tier
        ?? parseDescriptionTier(componentName)?.tier
        ?? tierHeaders.find((t) => componentName.includes(` - ${t}`))
        ?? null;

    if (tier == null) {
        const amount = Number(rateSet?.defaultRate?.amount ?? 0);
        return {
            kind: "base",
            componentName,
            amount: Number.isFinite(amount) ? amount : 0,
        };
    }

    const tierIndex = tierHeaders.indexOf(tier);
    if (tierIndex < 0) {
        return { kind: "skipped", componentName, reason: `unknown tier ${tier}` };
    }

    const amount = Number(rateSet?.specificRates?.[0]?.value?.amount ?? 0);
    return {
        kind: "tiered",
        componentName,
        tier,
        tierIndex,
        amount: Number.isFinite(amount) ? amount : 0,
    };
};

/**
 * Builds a step-by-step breakdown of billing rate totals for display in the UI.
 */
export const buildBillingRateTotalsBreakdown = (
    billingCategoryRates,
    componentsByName,
    fees,
    tierHeaders,
) => {
    const headers = tierHeaders ?? [];
    const base = [];
    const tiered = headers.map(() => []);
    const pmpm = [];
    const skipped = [];

    for (const cat of billingCategoryRates ?? []) {
        const line = resolveBillingRateTotalLine(cat, componentsByName, fees, headers);
        if (line.kind === "skipped") {
            skipped.push({ componentName: line.componentName, reason: line.reason });
        } else if (line.kind === "pmpm") {
            pmpm.push({ componentName: line.componentName, amount: line.amount });
        } else if (line.kind === "base") {
            base.push({ componentName: line.componentName, amount: line.amount });
        } else if (line.kind === "tiered") {
            tiered[line.tierIndex].push({
                componentName: line.componentName,
                amount: line.amount,
                tier: line.tier,
            });
        }
    }

    const baseSubtotal = base.reduce((sum, row) => sum + row.amount, 0);
    const totals = headers.map((_, index) => {
        const tieredSum = tiered[index].reduce((sum, row) => sum + row.amount, 0);
        return baseSubtotal + tieredSum;
    });

    return {
        tierHeaders: headers,
        base,
        baseSubtotal,
        tiered,
        pmpm,
        skipped,
        totals,
    };
};

export const buildComponentsByName = (componentPayloads) => {
    const map = new Map();
    (componentPayloads ?? []).forEach((wrapper) => {
        const name =
            wrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;
        if (name) map.set(name, wrapper);
    });
    return map;
};

// Maps a coverage tier header to the corresponding key on a plan's
// billingRateTotals object (as parsed from the XLSX file in step one).
const TIER_HEADER_TO_TOTALS_KEY = {
    "EE Only": "eeOnly",
    "EES / EE+1": "eesEePlus1",
    EEC: "eec",
    "FAM / EE+2+": "famEePlus2",
};

// Totals are dollar amounts, so a sub-cent tolerance avoids false mismatches
// caused by floating-point accumulation.
const totalsRoughlyEqual = (a, b) => Math.abs(a - b) < 0.005;

const getBenefitPlanId = (payload) =>
    payload?.benefitPlan ?? payload?.plan?.name ?? null;

// Computes XLSX totals-mismatch warnings and rate-collision warnings for each
// rate payload, returning a single merged summary. Use prefixRows: true (step
// three) to prepend "Row <n>, " to each warning line, or false (step four) to
// prefix with only the plan name label.
export const summarizeRateWarnings = (
    ratePayloads,
    plans,
    componentsByName,
    fees,
    uploadContext,
    rateCollisions,
    { prefixRows = false } = {},
) => {
    const warningIndexes = new Set();
    const warningEntries = [];
    const totalsByIndex = [];

    const planIdToIndex = new Map();
    (ratePayloads ?? []).forEach((payload, index) => {
        const planId = getBenefitPlanId(payload);
        if (planId != null && !planIdToIndex.has(planId)) planIdToIndex.set(planId, index);
    });

    // --- XLSX totals-mismatch warnings ---
    (ratePayloads ?? []).forEach((payload, index) => {
        const benefitPlanId = getBenefitPlanId(payload);
        const tierHeaders = getCoverageTierHeaders(uploadContext, benefitPlanId);
        const { totals } = computeBillingRateTotals(
            payload?.billingRatePayload?.billingRate?.billingCategoryRates,
            componentsByName,
            fees,
            tierHeaders,
        );
        totalsByIndex[index] = { tierHeaders, totals };

        const plan = (plans ?? []).find(
            (p) => p?.planId === benefitPlanId || p?.name === benefitPlanId,
        );
        const planTotals = plan?.billingRateTotals;
        if (!planTotals) return;

        // Only compare tiers the XLSX actually provides a total for.
        const mismatch = tierHeaders.some((tier, i) => {
            const key = TIER_HEADER_TO_TOTALS_KEY[tier];
            const expected = key ? planTotals[key] : null;
            if (expected == null) return false;
            return !totalsRoughlyEqual(Number(expected), Number(totals[i] ?? 0));
        });

        if (mismatch) {
            warningIndexes.add(index);
            const planName = plan?.planName ?? benefitPlanId ?? "";
            const prefix = prefixRows ? `Row ${index + 1}, ${planName}` : `${planName}`;
            warningEntries.push({ index, line: `${prefix}: Billing rate totals don't match the totals from the XLSX file` });
        }
    });

    // Rate collision amend warnings are handled via HRP reconciliation; omit here.
    warningEntries.sort((a, b) => (a.index ?? Infinity) - (b.index ?? Infinity));
    const warningLines = warningEntries.map((e) => e.line);

    return {
        totalsByIndex,
        hasWarnings: warningLines.length > 0,
        warningCount: warningIndexes.size,
        warningIndexes,
        warningLines,
    };
};

// Component collision amend warnings are handled via HRP reconciliation; omit here.
export const summarizeComponentWarnings = (
    _collisions,
    _componentPayloads,
    { prefixRows: _prefixRows = false } = {},
) => ({
    hasWarnings: false,
    warningCount: 0,
    warningLines: [],
    warningIndexes: new Set(),
});

export const LabelValue = ({ label, value, error }) => (
    <div className="billing-rate-builder__label-value">
        <p className="label billing-rate-builder__label">{label}</p>
        <p className="value">{value ?? ""}</p>
        {error && (
            <p style={{ color: "var(--red)", paddingTop: ".4rem", fontSize: ".85rem" }}>{error}</p>
        )}
    </div>
);

export const ListSection = ({ label, headers, rows }) => {
    const gridStyle = { gridTemplateColumns: `repeat(${headers.length}, 1fr)` };
    return (
        <div className="billing-rate-builder__section">
            <p className="sub-title billing-rate-builder__subtitle-emphasis">{label}</p>
            <div className="billing-rate-builder__list-wrap">
                <div className="billing-rate-builder__header-row" style={gridStyle}>
                    {headers.map((header, i) => (
                        <div key={i}>{header}</div>
                    ))}
                </div>
                {rows.length === 0 ? (
                    <div className="billing-rate-builder__list-empty">None</div>
                ) : (
                    rows.map((row, i) => {
                        const isLast = i === rows.length - 1;
                        const className = isLast
                            ? "billing-rate-builder__row billing-rate-builder__row--last"
                            : "billing-rate-builder__row";
                        return (
                            <div key={i} className={className} style={gridStyle}>
                                {headers.map((_, j) => (
                                    <div key={j}>{row[j] ?? ""}</div>
                                ))}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export const ModalHeader = ({ title, onEdit, isEditLoading = false }) => (
    <div className="billing-rate-builder__modal-header">
        <p className="title billing-rate-builder__modal-title">{title}</p>
        {onEdit && (
            <button
                type="button"
                className="btn edit-btn"
                onClick={onEdit}
                disabled={isEditLoading}
            >
                {isEditLoading ? "Loading..." : "Edit"}
            </button>
        )}
    </div>
);

const ALERT_VARIANT_CLASS = {
    error: "danger",
    warning: "warning",
    success: "success",
};

export const Alert = ({ variant = "error", title, body, footer }) => {
    const modifier = ALERT_VARIANT_CLASS[variant] ?? "danger";
    const lines = Array.isArray(body) ? body.filter((line) => line != null && line !== "") : [];
    return (
        <div className={`billing-rate-builder__alert billing-rate-builder__alert--${modifier}`}>
            {title && <p className="title billing-rate-builder__alert-title">{title}</p>}
            {lines.length > 0 && (
                <ul className="billing-rate-builder__alert-body-list">
                    {lines.map((line, i) => (
                        <li key={i}>{line}</li>
                    ))}
                </ul>
            )}
            {footer && <p className="billing-rate-builder__alert-footer">{footer}</p>}
        </div>
    );
};

export const FormHeader = ({ onBack, onSave, saveLabel = "Save", noBackTitle, disabled = false }) => (
    <div className="billing-rate-builder__modal-header">
        {onBack ? (
            <div
                className="backBtn"
                style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                onClick={onBack}
            >
                <IoChevronBack />
                {"Back"}
            </div>
        ) : noBackTitle ? (
            <p className="title billing-rate-builder__modal-title">{noBackTitle}</p>
        ) : (
            <div />
        )}
        {onSave && (
            <button type="button" className="btn edit-btn" onClick={onSave} disabled={disabled}>
                {saveLabel}
            </button>
        )}
    </div>
);

export const FormArrayEditor = ({
    name,
    label,
    headers,
    getRow,
    getRows,
    renderEditor,
    makeNew,
    canAdd = true,
    error,
    validateRow,
}) => {
    const { control } = useFormContext();
    const { fields, append, remove } = useFieldArray({ control, name });
    const watched = useWatch({ control, name }) ?? [];
    const [editingKey, setEditingKey] = useState(null);
    const gridStyle = { gridTemplateColumns: `repeat(${headers.length + 1}, 1fr)` };

    const handleAdd = () => {
        append(makeNew());
        setEditingKey(`${fields.length}:0`);
    };

    const handleRemove = (index) => {
        remove(index);
        const editingIndex = editingKey ? Number(editingKey.split(":")[0]) : null;
        if (editingIndex === index) setEditingKey(null);
        else if (editingIndex != null && index < editingIndex) {
            const editingRowIdx = editingKey.split(":")[1];
            setEditingKey(`${editingIndex - 1}:${editingRowIdx}`);
        }
    };

    const getCellRows = (fieldValue, i) =>
        getRows ? getRows(fieldValue, i) : [getRow(fieldValue, i)];

    return (
        <div className="billing-rate-builder__section">
            <p className="sub-title billing-rate-builder__subtitle-emphasis">{label}</p>
            {error && (
                <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{error}</p>
            )}
            <div className="billing-rate-builder__list-wrap">
                <div className="billing-rate-builder__header-row" style={gridStyle}>
                    {headers.map((h, i) => (
                        <div key={i}>{h}</div>
                    ))}
                    <div></div>
                </div>
                {fields.length === 0 ? (
                    <div className="billing-rate-builder__list-empty">None</div>
                ) : (
                    fields.map((field, i) => {
                        const isLastField = i === fields.length - 1;
                        const cellRows = getCellRows(watched[i] ?? {}, i);
                        return (
                            <div key={field.id}>
                                {cellRows.map((cells, rowIdx) => {
                                    const isLastRowOfField = rowIdx === cellRows.length - 1;
                                    const isLastVisualRow = isLastField && isLastRowOfField;
                                    const rowCls = isLastVisualRow
                                        ? "billing-rate-builder__row billing-rate-builder__row--last"
                                        : "billing-rate-builder__row";
                                    const rowKey = `${i}:${rowIdx}`;
                                    const isEditing = editingKey === rowKey;
                                    const rowValid =
                                        !validateRow || validateRow(watched[i] ?? {});
                                    const doneDisabled = isEditing && !rowValid;
                                    return (
                                        <div key={rowIdx}>
                                            <div className={rowCls} style={gridStyle}>
                                                {headers.map((_, j) => (
                                                    <div key={j}>{cells[j] ?? ""}</div>
                                                ))}
                                                <div className="billing-rate-builder__row-actions">
                                                    <span
                                                        className={
                                                            doneDisabled
                                                                ? "clickable-link disabled"
                                                                : "clickable-link"
                                                        }
                                                        style={{
                                                            cursor: doneDisabled
                                                                ? "not-allowed"
                                                                : "pointer",
                                                            opacity: doneDisabled ? 0.5 : 1,
                                                        }}
                                                        onClick={() => {
                                                            if (doneDisabled) return;
                                                            setEditingKey(isEditing ? null : rowKey);
                                                        }}
                                                    >
                                                        {isEditing ? "Done" : "Edit"}
                                                    </span>
                                                    <span
                                                        className="clickable-link danger"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => handleRemove(i)}
                                                    >
                                                        Remove
                                                    </span>
                                                </div>
                                            </div>
                                            {isEditing && (
                                                <div className="billing-rate-builder__array-editor">
                                                    {renderEditor(`${name}.${i}`, i)}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })
                )}
            </div>
            {canAdd && (
                <div className="billing-rate-builder__array-add">
                    <span
                        className="clickable-link"
                        style={{ cursor: "pointer" }}
                        onClick={handleAdd}
                    >
                        + Add
                    </span>
                </div>
            )}
        </div>
    );
};

export const BILLING_RATE_HEADERS_DEFAULT = ["Component", "Lock Date", "Rate Type", "Vendor", "Default Rate"];
export const BILLING_RATE_HEADERS_TIERED = [
    "Component",
    "Lock Date",
    "Rate Type",
    "Vendor",
    "Coverage Tier Name",
    "Specific Rate",
];

export const buildBillingRateRows = (defs, componentsByName, getDefMeta) => {
    let anyTiered = false;
    const rowsByDef = (defs ?? []).map((def) => {
        const { name, lockDate } = getDefMeta(def);
        const wrapper = componentsByName?.get(name);
        const info = getComponentRateInfo(wrapper);
        const vendor =
            wrapper?.billingRateComponentPayload?.billingRateComponent
                ?.definition?.rateClassifications?.rateSubCategory?.shortName ?? "";
        if (info.tiered) anyTiered = true;
        if (info.tiered && info.specificRates.length > 0) {
            return info.specificRates.map((sr) => ({
                name,
                lockDate,
                rateType: info.rateType,
                vendor,
                coverageTierName: sr?.members?.coverageTierName ?? "",
                amount: sr?.value?.amount ? `$${sr.value.amount}` : "",
            }));
        }
        return [
            {
                name,
                lockDate,
                rateType: info.rateType,
                vendor,
                coverageTierName: "",
                amount: info.defaultRate ? `$${info.defaultRate}` : "",
            },
        ];
    });
    return { rowsByDef, anyTiered };
};

export const getBillingRateHeaders = (anyTiered) =>
    anyTiered ? BILLING_RATE_HEADERS_TIERED : BILLING_RATE_HEADERS_DEFAULT;

export const billingRateRowToCells = (row, anyTiered) =>
    anyTiered
        ? [row.name, row.lockDate, row.rateType, row.vendor, row.coverageTierName, row.amount]
        : [row.name, row.lockDate, row.rateType, row.vendor, row.amount];

export const getComponentRateInfo = (wrapper) => {
    const c = wrapper?.billingRateComponentPayload?.billingRateComponent;
    const defKey = getDefinitionKey(c);
    const rateSet = getRateSet(c);
    const rateType = defKey === "perSubscriptionPerMonthBillingRateDefinition"
        ? "PSPM"
        : defKey === "perMemberPerMonthBillingRateDefinition"
            ? "PMPM"
            : "";
    return {
        tiered: Boolean(wrapper?.tiered),
        rateType,
        specificRates: Array.isArray(rateSet?.specificRates) ? rateSet.specificRates : [],
        defaultRate: rateSet?.defaultRate?.amount ?? "",
    };
};

export const getRateTypeAndAmount = (def) => {
    const pspm = def?.perSubscriptionPerMonthBillingRateDefinition;
    if (pspm) {
        return {
            rateType: "PSPM",
            defaultRate: pspm?.pspmRateSet?.defaultRate?.amount ?? "",
        };
    }
    const pmpm = def?.perMemberPerMonthBillingRateDefinition;
    if (pmpm) {
        return {
            rateType: "PMPM",
            defaultRate: pmpm?.pmpmRateSet?.defaultRate?.amount ?? "",
        };
    }
    return { rateType: "", defaultRate: "" };
};

export const mappingKeyOf = (setName) =>
    Object.entries(COVERAGE_TIER_SET_NAMES).find(([, v]) => v === setName)?.[0];

export const parseLockDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === "string") {
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
        if (match) {
            const [, y, m, d] = match;
            return new Date(Number(y), Number(m) - 1, Number(d));
        }
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

export const formatLockDate = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.slice(0, 10);
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

export const PARTIAL_INTERVAL_FULL_PERIOD = "Full Period";
export const PARTIAL_INTERVAL_PRORATED = "Prorated";
export const PARTIAL_INTERVAL_WASH = "Wash";

export const PARTIAL_INTERVAL_OPTIONS = [
    { value: PARTIAL_INTERVAL_FULL_PERIOD, label: PARTIAL_INTERVAL_FULL_PERIOD },
    { value: PARTIAL_INTERVAL_PRORATED, label: PARTIAL_INTERVAL_PRORATED },
    { value: PARTIAL_INTERVAL_WASH, label: PARTIAL_INTERVAL_WASH },
];

export const PARTIAL_INTERVAL_FIELD_CONFIG = {
    [PARTIAL_INTERVAL_FULL_PERIOD]: {
        name: "minimumEnrolledDaysForFullPeriodBilling",
        label: "Minimum Enrolled Days For Full Billing Period",
    },
    [PARTIAL_INTERVAL_PRORATED]: {
        name: "prorationByNumberOfDaysPerMonth",
        label: "Proration By Number Of Days Per Month",
    },
    [PARTIAL_INTERVAL_WASH]: {
        name: "washDay",
        label: "Wash Day",
    },
};

export const PARTIAL_INTERVAL_FIELD_NAMES = Object.values(PARTIAL_INTERVAL_FIELD_CONFIG).map(
    (c) => c.name,
);

export const partialIntervalMethodValue = (option) =>
    (option && typeof option === "object" ? option.value : option) ?? null;

export const RATE_TYPE_OPTIONS = [
    { value: "perSubscriptionPerMonthBillingRateDefinition", label: "Per Subscription Per Month" },
    { value: "perMemberPerMonthBillingRateDefinition", label: "Per Member Per Month" },
];

export const COVERAGE_TIER_SET_OPTIONS = Object.values(COVERAGE_TIER_SET_NAMES).map((v) => ({
    value: v,
    label: v,
}));

export const RATE_SET_KEY_BY_DEF_KEY = {
    perSubscriptionPerMonthBillingRateDefinition: "pspmRateSet",
    perMemberPerMonthBillingRateDefinition: "pmpmRateSet",
};

export const getDefinitionKey = (component) => {
    if (component?.perSubscriptionPerMonthBillingRateDefinition) {
        return "perSubscriptionPerMonthBillingRateDefinition";
    }
    if (component?.perMemberPerMonthBillingRateDefinition) {
        return "perMemberPerMonthBillingRateDefinition";
    }
    return null;
};

export const getRateSet = (component) => {
    const defKey = getDefinitionKey(component);
    if (!defKey) return null;
    const rateSetKey = RATE_SET_KEY_BY_DEF_KEY[defKey];
    return component?.[defKey]?.[rateSetKey] ?? null;
};

// Adapts a component payload into the component edit form's value shape. Shared
// by the edit form (default values) and payload validation so both use the same
// payload -> form-values mapping.
export const buildComponentDefaultValues = (payload) => {
    const c = payload?.billingRateComponentPayload?.billingRateComponent ?? {};
    const defKey = getDefinitionKey(c) ?? "perSubscriptionPerMonthBillingRateDefinition";
    const rateSet = getRateSet(c) ?? {};
    const setName = c?.definition?.coverageTierSetName ?? "";
    const setNameOption = setName ? { value: setName, label: setName } : null;
    const rateSubCategory = c?.definition?.rateClassifications?.rateSubCategory;
    // The matched codeEntry lives on the wrapper's sidecar match metadata, not on
    // the payload's rateSubCategory (which is limited to { codeSetName, shortName }).
    const codeEntry = payload?.subCategoryMatch?.codeEntry;
    // Only pre-populate the select when BOTH codeEntry and shortName are present.
    // If only shortName exists the sub category wasn't matched during lookup
    // enrichment; leaving it null lets the validator report a precise error.
    const rateSubCategoryOption =
        codeEntry && rateSubCategory?.shortName
            ? {
                value: codeEntry,
                label: rateSubCategory.shortName,
            }
            : null;
    return {
        tiered: Boolean(payload?.tiered),
        componentName: c?.componentName ?? "",
        description: c?.description ?? "",
        componentId: c?.definition?.rateClassifications?.componentId ?? "",
        rateSubCategory: rateSubCategoryOption,
        rateType: toOption(defKey, RATE_TYPE_OPTIONS),
        coverageTierSetName: setNameOption,
        specificRates: Array.isArray(rateSet?.specificRates)
            ? rateSet.specificRates.map((r) => ({
                coverageTierName: r?.members?.coverageTierName
                    ? { value: r.members.coverageTierName, label: r.members.coverageTierName }
                    : null,
                amount: r?.value?.amount ?? "",
            }))
            : [],
        defaultRateAmount: rateSet?.defaultRate?.amount ?? "",
    };
};

const toComponentOption = (name) =>
    name == null || name === "" ? null : { value: name, label: name };

const toPartialIntervalOption = (value) => {
    const v = partialIntervalMethodValue(value);
    if (!v) return null;
    return PARTIAL_INTERVAL_OPTIONS.find((o) => o.value === v) ?? { value: v, label: v };
};

const buildAccountOption = (accountHccId, accountName, resolvedAccountRow) => {
    if (resolvedAccountRow?.accountHccId) {
        return {
            value: resolvedAccountRow.accountHccId,
            label: `${resolvedAccountRow.accountName} (${resolvedAccountRow.accountHccId})`,
            additional: resolvedAccountRow,
        };
    }
    if (!accountHccId) return null;
    return {
        value: accountHccId,
        label: accountName ? `${accountName} (${accountHccId})` : accountHccId,
        additional: {
            accountHccId,
            accountName: accountName ?? null,
        },
    };
};

const buildBenefitPlanOption = (benefitPlanName, benefitPlanHccId) => {
    if (!benefitPlanName) return null;
    const label = benefitPlanHccId ? `${benefitPlanName} (${benefitPlanHccId})` : benefitPlanName;
    return { value: benefitPlanName, label, additional: { benefitPlanHccId } };
};

const buildBillingCategoryOption = (billingCategory, billingCategoryRows, billingCategoryShortNames) => {
    const codeEntry = billingCategory?.codeEntry ?? "";
    if (!codeEntry) return null;

    // Edit-form path: lookup rows are provided — search for a match.
    if (Array.isArray(billingCategoryRows)) {
        const matchedCategory = billingCategoryRows.find((r) => r?.cdEntryTxt === codeEntry);
        if (matchedCategory) {
            const shortNm = matchedCategory.shortNm ?? "";
            return {
                value: codeEntry,
                label: `${shortNm} (${codeEntry})`,
                additional: { shortName: shortNm },
            };
        }
        // Rows provided but no match (e.g. still loading after draft resume) —
        // fall back to the shortName recorded on the payload dictionary.
        const fallbackShortName = billingCategoryShortNames?.[codeEntry];
        if (fallbackShortName) {
            return {
                value: codeEntry,
                label: `${fallbackShortName} (${codeEntry})`,
                additional: { shortName: fallbackShortName },
            };
        }
        return null;
    }

    // Validation path: no lookup rows. Use the shortName recorded on the wrapper's
    // billingCategoryShortNames dictionary by applyFetchedCategoryLookups when a
    // match was found. If absent the category was never matched; leave the field
    // null so the validator can report a precise error.
    const shortName = billingCategoryShortNames?.[codeEntry];
    if (!shortName) return null;
    return {
        value: codeEntry,
        label: `${shortName} (${codeEntry})`,
        additional: { shortName },
    };
};

// Adapts a rate payload into the rate edit form's value shape. The optional
// lookup rows enrich the select labels; without them (e.g. payload validation)
// the option values are still resolved from the payload.
export const buildRateDefaultValues = (payload, resolvedAccountRow, billingCategoryRows) => {
    const rate = payload?.billingRatePayload?.billingRate ?? {};
    const accountHccId = payload?.account ?? rate?.account ?? "";
    const accountName = payload?.accountName ?? rate?.accountName ?? null;
    const benefitPlanHccId = payload?.benefitPlan ?? payload?.plan?.name ?? "";
    const benefitPlanName = rate?.benefitPlan ?? "";
    const billingCategoryShortNames = payload?.billingCategoryShortNames;
    return {
        account: buildAccountOption(accountHccId, accountName, resolvedAccountRow),
        benefitPlan: buildBenefitPlanOption(benefitPlanName, benefitPlanHccId),
        billingCategoryRates: (rate?.billingCategoryRates ?? []).map((cat) => ({
            billingCategory: buildBillingCategoryOption(cat?.billingCategory, billingCategoryRows, billingCategoryShortNames),
            partialIntervalCalculationMethod: toPartialIntervalOption(
                cat?.partialIntervalCalculationMethod,
            ),
            minimumEnrolledDaysForFullPeriodBilling:
                cat?.minimumEnrolledDaysForFullPeriodBilling ?? "",
            prorationByNumberOfDaysPerMonth: cat?.prorationByNumberOfDaysPerMonth ?? "",
            washDay: cat?.washDay ?? "",
            billingPartyPercentages: (cat?.billingPartyPercentages ?? []).map((b) => ({
                billingParty: b?.billingParty ?? "",
                billPercentage: b?.billPercentage ?? "",
            })),
            billingRateDefinitions: (cat?.billingRateDefinitions ?? []).map((d) => ({
                ...d,
                componentReference: {
                    ...(d?.componentReference ?? {}),
                    component: toComponentOption(d?.componentReference?.component),
                    lockDate: parseLockDate(d?.componentReference?.lockDate),
                },
            })),
        })),
    };
};

function resolveComponentFeeCode(fees, feeRow) {
    if (feeRow == null) return null;
    const fee = (fees ?? []).find((entry) => entry?.rowNumber === feeRow);
    const lookup1 = fee?.lookup1;
    if (lookup1 == null || lookup1 === '') return null;
    return String(lookup1).trim();
}

function shouldResyncRateDefinition(cat, def, oldComponentName, feeCode) {
    if (def?.componentReference?.component !== oldComponentName) return false;
    if (feeCode) {
        return String(cat?.billingCategory?.codeEntry ?? '').trim() === feeCode;
    }

    const matching = (cat?.billingRateDefinitions ?? []).filter(
        (entry) => entry?.componentReference?.component === oldComponentName,
    );
    return matching.length === 1;
}

export const resyncRatesForComponent = (ratePayloads, oldComponentName, newWrapper, { fees } = {}) => {
    if (!Array.isArray(ratePayloads) || !oldComponentName) return ratePayloads;
    const newName = newWrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;
    if (!newName || newName === oldComponentName) return ratePayloads;

    const feeCode = resolveComponentFeeCode(fees, newWrapper?.feeRow);

    return ratePayloads.map((wrapper) => {
        const billingRate = wrapper?.billingRatePayload?.billingRate;
        const componentNames = wrapper?.componentNames;
        const referencesOld =
            (Array.isArray(componentNames) && componentNames.includes(oldComponentName)) ||
            (billingRate?.billingCategoryRates ?? []).some((cat) =>
                (cat?.billingRateDefinitions ?? []).some(
                    (def) => def?.componentReference?.component === oldComponentName,
                ),
            );
        if (!referencesOld) return wrapper;

        let updatedDefinitionCount = 0;

        // Spreading `...cat` preserves the category's key order, only replacing
        // billingRateDefinitions in place. componentReference is rebuilt
        // explicitly (lockDate, component) so its order never depends on the source object.
        const updatedCats = (billingRate?.billingCategoryRates ?? []).map((cat) => ({
            ...cat,
            billingRateDefinitions: (cat?.billingRateDefinitions ?? []).map((def) => {
                if (!shouldResyncRateDefinition(cat, def, oldComponentName, feeCode)) return def;
                updatedDefinitionCount += 1;
                return {
                    componentReference: {
                        lockDate: def?.componentReference?.lockDate,
                        component: newName,
                    },
                };
            }),
        }));

        if (updatedDefinitionCount === 0) return wrapper;

        let renamedInComponentList = false;
        const updatedComponentNames = Array.isArray(componentNames)
            ? componentNames.map((name) => {
                if (renamedInComponentList || name !== oldComponentName) return name;
                renamedInComponentList = true;
                return newName;
            })
            : componentNames;

        return {
            ...wrapper,
            componentNames: updatedComponentNames,
            billingRatePayload: {
                ...wrapper.billingRatePayload,
                billingRate: {
                    billingCategoryRates: updatedCats,
                    benefitPlan: billingRate?.benefitPlan,
                    account: billingRate?.account,
                },
            },
        };
    });
};

export const removeComponentReferencesFromRates = (ratePayloads, componentName) => {
    if (!Array.isArray(ratePayloads) || !componentName) return ratePayloads;

    const result = [];
    for (const wrapper of ratePayloads) {
        const billingRate = wrapper?.billingRatePayload?.billingRate;
        const componentNames = wrapper?.componentNames;
        const referencesIt =
            (Array.isArray(componentNames) && componentNames.includes(componentName)) ||
            (billingRate?.billingCategoryRates ?? []).some((cat) =>
                (cat?.billingRateDefinitions ?? []).some(
                    (def) => def?.componentReference?.component === componentName,
                ),
            );
        if (!referencesIt) {
            result.push(wrapper);
            continue;
        }

        // `...cat` preserves each category's key order; only billingRateDefinitions
        // is replaced (filtered) in place.
        const updatedCats = (billingRate?.billingCategoryRates ?? [])
            .map((cat) => ({
                ...cat,
                billingRateDefinitions: (cat?.billingRateDefinitions ?? []).filter(
                    (def) => def?.componentReference?.component !== componentName,
                ),
            }))
            .filter((cat) => (cat?.billingRateDefinitions?.length ?? 0) > 0);

        if (updatedCats.length === 0) continue;

        result.push({
            ...wrapper,
            componentNames: Array.isArray(componentNames)
                ? componentNames.filter((n) => n !== componentName)
                : componentNames,
            billingRatePayload: {
                ...wrapper.billingRatePayload,
                billingRate: {
                    billingCategoryRates: updatedCats,
                    benefitPlan: billingRate?.benefitPlan,
                    account: billingRate?.account,
                },
            },
        });
    }
    return result;
};

// When a renewalRequired component is resolved (an amount is added in step 2),
// it must be injected into the billingCategoryRates of every rate payload that
// belongs to one of the component's plans. The original build skipped
// addBillingRateDefinition entirely, so the component has no entry in
// componentNames or billingCategoryRates of any rate payload.
// Matches rate payloads by plan name, borrows the lockDate from an existing
// definition in each matched payload, and resolves the billing category code
// from billingCategoryRows using the same desc/lookup2 key as the original build.
export const injectRenewalResolvedDefinitions = (ratePayloads, componentWrapper, { fees = [], billingCategoryRows = [] } = {}) => {
    if (!Array.isArray(ratePayloads) || !componentWrapper) return ratePayloads;

    const componentName =
        componentWrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;
    if (!componentName) return ratePayloads;

    // The plan names this component belongs to.
    const componentPlanNames = new Set(
        (componentWrapper?.plans ?? []).map((p) => p?.name).filter(Boolean),
    );
    if (componentPlanNames.size === 0) return ratePayloads;

    // Resolve the billing category codeEntry the same way the original build does:
    // try "desc lookup2" key first, then plain desc key, using the same lookup
    // map (with colon-tier aliases and BILLING_CATEGORY_ALIASES applied).
    const fee = (fees ?? []).find((f) => f?.rowNumber === componentWrapper?.feeRow);
    const descKey = String(fee?.description ?? componentWrapper?.feeDescription ?? "").trim().toLowerCase();
    const tierKey = fee?.lookup2 ? `${descKey} ${String(fee.lookup2).trim().toLowerCase()}` : null;
    const categoryLookup = buildBillingCategoryLookup(billingCategoryRows);
    const codeEntry = (tierKey ? categoryLookup?.get(tierKey) : null) ?? categoryLookup?.get(descKey) ?? undefined;
    const shortNm = codeEntry
        ? ((billingCategoryRows ?? []).find((r) => r?.cdEntryTxt === codeEntry)?.shortNm ?? null)
        : null;

    return ratePayloads.map((wrapper) => {
        const planName = wrapper?.plan?.name ?? wrapper?.benefitPlan;
        if (!componentPlanNames.has(planName)) return wrapper;

        const billingRate = wrapper?.billingRatePayload?.billingRate;
        const cats = billingRate?.billingCategoryRates ?? [];

        // Skip if already referenced (e.g. imported file that was already correct).
        const alreadyReferenced = cats.some((cat) =>
            (cat?.billingRateDefinitions ?? []).some(
                (def) => def?.componentReference?.component === componentName,
            ),
        );
        if (alreadyReferenced) return wrapper;

        // Borrow the lockDate from the first existing definition in this payload.
        const lockDate = cats
            .flatMap((cat) => cat?.billingRateDefinitions ?? [])
            .find((def) => def?.componentReference?.lockDate)
            ?.componentReference?.lockDate ?? undefined;

        const newCat = {
            partialIntervalCalculationMethod: "Wash",
            washDay: 15,
            billingPartyPercentages: [{ billingParty: "Account", billPercentage: 100 }],
            billingRateDefinitions: [{ componentReference: { lockDate, component: componentName } }],
            billingCategory: { codeSetName: "BillingCategory", codeEntry },
        };

        const updatedShortNames = codeEntry && shortNm
            ? { ...wrapper.billingCategoryShortNames, [codeEntry]: shortNm }
            : wrapper.billingCategoryShortNames;

        return {
            ...wrapper,
            componentNames: Array.isArray(wrapper.componentNames)
                ? [...wrapper.componentNames, componentName]
                : wrapper.componentNames,
            billingCategoryShortNames: updatedShortNames,
            billingRatePayload: {
                ...wrapper.billingRatePayload,
                billingRate: {
                    ...billingRate,
                    billingCategoryRates: [...cats, newCat],
                },
            },
        };
    });
};

export const toOption = (value, options) => {
    if (value == null || value === "") return null;
    const found = options?.find((o) => o.value === value);
    if (found) return found;
    return { value, label: value };
};

export const applyEffectiveDateToRatePayloads = (
    ratePayloads,
    effectiveDate,
    { preserveLockDateComponents } = {},
) => {
    if (!Array.isArray(ratePayloads)) return ratePayloads;
    const preserve = preserveLockDateComponents instanceof Set
        ? preserveLockDateComponents
        : new Set(preserveLockDateComponents ?? []);
    const formattedLockDate = formatLockDate(effectiveDate);

    return ratePayloads.map((wrapper) => {
        const billingRate = wrapper?.billingRatePayload?.billingRate;
        if (!billingRate) return wrapper;
        return {
            ...wrapper,
            billingRatePayload: {
                ...wrapper.billingRatePayload,
                billingRate: {
                    ...billingRate,
                    billingCategoryRates: (billingRate.billingCategoryRates ?? []).map((cat) => ({
                        ...cat,
                        billingRateDefinitions: (cat.billingRateDefinitions ?? []).map((def) => {
                            const componentName = def?.componentReference?.component;
                            if (componentName && preserve.has(componentName)) {
                                return def;
                            }
                            return {
                                ...def,
                                componentReference: {
                                    ...def.componentReference,
                                    lockDate: formattedLockDate,
                                },
                            };
                        }),
                    })),
                },
            },
        };
    });
};
